<script setup>
import { ref } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import ProfilePictureUploadSimple from '@/Components/Forms/ProfilePictureUploadSimple.vue'
import ProfileVideoUpload from '@/Components/Forms/ProfileVideoUpload.vue'
import { useProfilePictureUpload } from '@/composables/useProfilePictureUpload'
import teacher from '@/routes/teacher'

const props = defineProps({
  user: Object,
})

// Get user slug for file uploads
const userSlug = props.user?.slug || ''

// Setup profile picture upload composable (use teacher files endpoint)
const { uploadProfilePicture, isUploading: isUploadingPicture } = useProfilePictureUpload(
  userSlug,
  '/teacher/files/signed-upload-url'
)

const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

// Profile media state - initialize directly from props
const avatarUrl = ref(props.user?.avatar_url || null)
const profileVideo = ref({
  upload_id: props.user?.teacher_profile?.intro_video?.upload_id || null,
  playback_id: props.user?.teacher_profile?.intro_video?.playback_id || null
})

const handlePhotoUpdate = (value) => {
  avatarUrl.value = value
  if (errors.value.avatar_url) {
    delete errors.value.avatar_url
  }
}

const handleVideoUpdate = (uploadId) => {
  profileVideo.value.upload_id = uploadId
  if (uploadId === null) {
    profileVideo.value.playback_id = null
  }
  if (errors.value.video) {
    delete errors.value.video
  }
}

const handleVideoComplete = (uploadId) => {
  profileVideo.value.upload_id = uploadId
}

const saveMedia = async () => {
  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const payload = {
      avatar_url: avatarUrl.value,
      intro_video: profileVideo.value.upload_id ? {
        upload_id: profileVideo.value.upload_id,
        playback_id: profileVideo.value.playback_id
      } : null,
    }

    const response = await fetch('/teacher/settings/media', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify(payload)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key]) ? data.errors[key][0] : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    successMessage.value = 'Media updated successfully!'
    loading.value = false

    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- Profile Media Section -->
      <div class="bg-white rounded-2xl p-5 md:p-10">
        <div class="mb-8">
          <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">Profile Media</h2>
          <p class="text-gray-600">Upload your profile picture and optionally a video introduction to help students connect with you.</p>
        </div>

        <div class="space-y-6">
          <!-- Profile Picture Upload -->
          <ProfilePictureUploadSimple
            :model-value="avatarUrl"
            :upload-function="uploadProfilePicture"
            :is-uploading="isUploadingPicture"
            :error="errors.avatar_url"
            @update:model-value="handlePhotoUpdate"
          />

          <!-- Profile Video Upload -->
          <ProfileVideoUpload
            :model-value="profileVideo.upload_id"
            :playback-id="profileVideo.playback_id"
            :upload-url-endpoint="teacher.settings.media.muxUploadUrl.url()"
            :error="errors.video"
            :tall="true"
            @update:model-value="handleVideoUpdate"
            @upload:complete="handleVideoComplete"
          />
        </div>
      </div>

      <!-- Save Button -->
      <div class="flex justify-end pt-6 border-t border-gray-200">
        <button
          @click="saveMedia"
          :disabled="loading"
          class="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-base"
        >
          <span v-if="loading" class="material-symbols-outlined animate-spin mr-2 text-xl">progress_activity</span>
          {{ loading ? 'Saving...' : 'Save Changes' }}
          <span v-if="!loading" class="material-symbols-outlined ml-2 text-xl">check</span>
        </button>
      </div>
    </div>
  </SettingsLayout>
</template>
